import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RoomDisplay, RoomImage } from '../../../models/room.model';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-room-detail-modal',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="modal-overlay" (click)="close()">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <button class="close-btn" (click)="close()" aria-label="Cerrar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <div class="modal-body">
          <!-- Gallery Section -->
          <div class="gallery-section">
            <div class="main-image-container">
              <img [src]="activeImage" [alt]="room?.name" class="main-image fade-in">
            </div>
            @if (room?.allImages && room!.allImages!.length > 1) {
              <div class="thumbnails-container">
                @for (img of room?.allImages; track img.id) {
                  <button 
                    class="thumbnail-btn" 
                    [class.active]="activeImage === img.imageUrl"
                    (click)="setActiveImage(img.imageUrl)">
                    <img [src]="img.imageUrl" [alt]="room?.name">
                  </button>
                }
              </div>
            }
          </div>

          <!-- Info Section -->
          <div class="info-section">
            <div class="header">
              <span class="category">{{ room?.bed }}</span>
              <h2 class="title">{{ room?.name }}</h2>
              <div class="price-wrap">
                <span class="amount">{{ room?.price }}</span>
                <span class="period">/ noche</span>
              </div>
            </div>

            <div class="specs-grid">
              <div class="spec-card">
                <div class="icon-wrap">📍</div>
                <div class="spec-label">Ubicación</div>
                <div class="spec-value">{{ room?.size }}</div>
              </div>
              <div class="spec-card">
                <div class="icon-wrap">👥</div>
                <div class="spec-label">Capacidad</div>
                <div class="spec-value">{{ room?.capacity }}</div>
              </div>
              <div class="spec-card">
                <div class="icon-wrap">🛏️</div>
                <div class="spec-label">Camas</div>
                <div class="spec-value">{{ room?.bed }}</div>
              </div>
            </div>

            <div class="description-wrap">
              @if (room?.significado) {
                <div class="significado-box">
                  <p class="meaning-text">{{ room?.significado }}</p>
                </div>
              }
              
              <div class="full-description">
                <h3>Acerca de esta habitación</h3>
                <p>{{ room?.description || 'Experimenta el máximo confort en nuestra habitación diseñada con detalles andinos tradicionales y comodidades modernas para un descanso inolvidable.' }}</p>
              </div>

            </div>

            <div class="actions">
              <a [routerLink]="['/reservar']" 
                 [queryParams]="{
                   roomId: room?.id, 
                   checkIn: room?.searchContext?.checkIn,
                   checkOut: room?.searchContext?.checkOut,
                   adults: room?.searchContext?.adults,
                   children: room?.searchContext?.children,
                   step: 2
                 }" 
                 class="btn btn-primary btn-block">
                Reservar Ahora
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.85); /* Darker backdrop */
      backdrop-filter: blur(10px);
      z-index: 9999; /* Even higher z-index */
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      animation: modalFadeIn 0.3s ease-out;
    }

    .modal-content {
      background: #fdfaf6;
      border-radius: 20px;
      width: 100%;
      max-width: 1100px;
      max-height: 90vh;
      overflow-y: auto;
      position: relative;
      box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.7);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    @keyframes modalFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .close-btn {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: white;
      border: none;
      box-shadow: 0 4px 10px rgba(0,0,0,0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10;
      transition: all 0.2s;
      color: #333;

      &:hover {
        background: var(--color-primary, #b71c1c);
        color: white;
        transform: rotate(90deg);
      }
    }

    .modal-body {
      display: grid;
      grid-template-columns: 1.2fr 1fr;
      gap: 0;
      min-height: 500px;

      @media (max-width: 850px) {
        grid-template-columns: 1fr;
      }
    }

    /* Gallery */
    .gallery-section {
      padding: 30px;
      background: #f5f5f5;
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .main-image-container {
      width: 100%;
      height: 400px;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 10px 20px rgba(0,0,0,0.1);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .thumbnails-container {
      display: flex;
      gap: 10px;
      overflow-x: auto;
      padding: 5px 0;
      scrollbar-width: thin;

      &::-webkit-scrollbar { height: 4px; }
      &::-webkit-scrollbar-thumb { background: #ccc; border-radius: 10px; }
    }

    .thumbnail-btn {
      width: 80px;
      height: 60px;
      border-radius: 8px;
      overflow: hidden;
      flex-shrink: 0;
      border: 2px solid transparent;
      padding: 0;
      cursor: pointer;
      transition: all 0.2s;

      &.active { border-color: var(--color-primary, #b71c1c); transform: scale(0.95); }
      &:hover { opacity: 0.8; }

      img { width: 100%; height: 100%; object-fit: cover; }
    }

    /* Info */
    .info-section {
      padding: 40px;
      display: flex;
      flex-direction: column;
      gap: 25px;
    }

    .header {
      .category {
        display: inline-block;
        padding: 4px 12px;
        background: rgba(183, 28, 28, 0.1);
        color: var(--color-primary, #b71c1c);
        font-size: 0.8rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        border-radius: 20px;
        margin-bottom: 10px;
      }

      .title {
        font-size: 2.2rem;
        color: #2c3e50;
        margin: 0 0 10px 0;
      }

      .price-wrap {
        .amount { font-size: 1.5rem; font-weight: 700; color: var(--color-primary, #b71c1c); }
        .period { color: #888; margin-left: 5px; }
      }
    }

    .specs-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 15px;
    }

    .spec-card {
      background: white;
      padding: 15px 10px;
      border-radius: 12px;
      text-align: center;
      border: 1px solid #eee;

      .icon-wrap { font-size: 1.5rem; margin-bottom: 5px; }
      .spec-label { font-size: 0.75rem; color: #888; text-transform: uppercase; }
      .spec-value { font-weight: 600; color: #444; font-size: 0.9rem; }
    }

    .description-wrap {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .significado-box {
      background: #fff8f0;
      padding: 15px;
      border-left: 4px solid #C86A4A;
      border-radius: 4px;
      
      .meaning-text {
        color: #5d4037;
        font-style: italic;
        margin: 0;
        font-size: 1.05rem;
        line-height: 1.5;
      }
    }

    h3 {
      font-size: 1.1rem;
      color: #2c3e50;
      margin-bottom: 10px;
      border-bottom: 1px solid #eee;
      padding-bottom: 5px;
    }

    .full-description p {
      color: #666;
      line-height: 1.6;
      margin: 0;
    }


    .actions {
      margin-top: auto;
      padding-top: 20px;
    }

    .btn-block {
      width: 100%;
      text-align: center;
      padding: 15px;
      font-size: 1.1rem;
      border-radius: 10px;
      text-decoration: none;
      display: block;
    }

    .btn-primary {
      background: var(--color-primary, #b71c1c);
      color: white;
      font-weight: 600;
      transition: all 0.2s;

      &:hover { background: #901616; transform: translateY(-2px); box-shadow: 0 5px 15px rgba(183, 28, 28, 0.3); }
    }

    .fade-in { animation: fadeIn 0.4s ease-out; }

    @keyframes fadeIn {
      from { opacity: 0; transform: scale(0.98); }
      to { opacity: 1; transform: scale(1); }
    }

    @media (max-width: 480px) {
      .header .title { font-size: 1.8rem; }
      .specs-grid { grid-template-columns: 1fr; }
      .info-section { padding: 25px; }
      .gallery-section { padding: 15px; height: 300px; }
      .main-image-container { height: 250px; }
    }
  `]
})
export class RoomDetailModalComponent implements OnChanges, OnDestroy {
  @Input() room: RoomDisplay | null = null;
  @Output() closeEvent = new EventEmitter<void>();

  activeImage: string = '';

  ngOnChanges(changes: SimpleChanges) {
    if (changes['room'] && this.room) {
      this.activeImage = this.room.image || (this.room.allImages && this.room.allImages.length > 0 ? this.room.allImages[0].imageUrl : '');
    }
  }

  ngOnDestroy() {
    // Restore body scroll when the modal is destroyed
    document.body.style.overflow = '';
  }

  setActiveImage(url: string) {
    this.activeImage = url;
  }

  close() {
    this.closeEvent.emit();
  }
}
